import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import {
  Settings, MessageSquare, MessageCircle, Users,
  LogOut, BookOpen, BarChart3, Eye, Database, UserCircle, KeyRound, TrendingUp, Mail
} from "lucide-react";
import axios from "axios";
import AgentLogin from "./AgentLogin";
import useEnhancedSessionsStream from "../hooks/useSessionsStream";
import { useAuth } from "../contexts/AuthContext";
import { logger } from "../utils/logger";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper: Map URL path to active tab
const URL_TAB_MAPPINGS = [
  { pattern: "/admin/visitors", tab: "visitors" },
  { pattern: "/admin/traffic", tab: "traffic" },
  { pattern: "/admin/chats", tab: "chats" },
  { pattern: "/admin/knowledge", tab: "knowledge" },
  { pattern: "/admin/feedback", tab: "feedback" },
  { pattern: "/admin/settings", tab: "settings" },
  { pattern: "/admin/agents", tab: "agents" },
  { pattern: "/admin/maintenance", tab: "maintenance" },
  { pattern: "/admin/personas", tab: "personas" },
  { pattern: "/admin/account", tab: "account" },
  { pattern: "/admin/offline-messages", tab: "offline-messages" },
];

const getTabFromPath = (path) => {
  if (path === "/admin" || path === "/admin/") return "dashboard";
  for (const mapping of URL_TAB_MAPPINGS) {
    if (path.includes(mapping.pattern)) return mapping.tab;
  }
  return "dashboard";
};

// Helper: Page title configuration by tab
const PAGE_TITLE_CONFIG = {
  dashboard: { title: "Dashboard", subtitle: "Overview of your chat performance and metrics" },
  visitors: { title: "Visitors", subtitle: "Track all visitors with detailed analytics and insights" },
  traffic: { title: "Traffic", subtitle: "Track all site traffic and visitor history" },
  chats: { title: "Conversations", getSubtitle: (count) => `Managing ${count} active conversations` },
  knowledge: { title: "Knowledge Base", subtitle: "Manage AI training content and knowledge" },
  feedback: { title: "Feedback", subtitle: "Review bot performance: answered and untrained questions" },
  settings: { title: "Settings", subtitle: "Configure widget appearance and behavior" },
  agents: { title: "Agent Management", subtitle: "Manage team members and permissions" },
  maintenance: { title: "System Maintenance", subtitle: "Manage database storage and cleanup old sessions" },
  personas: { title: "Bot Personas", subtitle: "Configure dynamic bot personalities based on user intent" },
  account: { title: "Account Settings", subtitle: "Change your password and view account details" },
  "offline-messages": { title: "Offline Messages", subtitle: "Messages left by visitors when chatbot was offline" },
};

const getPageTitleConfig = (tab, sessionCount = 0) => {
  const config = PAGE_TITLE_CONFIG[tab] || PAGE_TITLE_CONFIG.dashboard;
  const subtitle = config.getSubtitle ? config.getSubtitle(sessionCount) : config.subtitle;
  return { title: config.title, subtitle: subtitle || "" };
};

// Helper: Play notification sound with error handling
const playNotificationSound = (audioFile, volume = 0.5) => {
  try {
    const audio = new Audio(audioFile);
    audio.volume = volume;
    audio.play().catch(err => console.log('Audio play failed:', err));
  } catch (err) {
    console.log('Audio creation failed:', err);
  }
};

// Helper: Show browser notification if permission granted
const showBrowserNotification = (title, body, permission) => {
  if (permission === "granted") {
    new Notification(title, { body, icon: "/favicon.ico" });
  }
};

// Helper: Get visitor location string for notification
const getVisitorLocationString = (visitorInfo) => {
  if (visitorInfo?.city) {
    return `${visitorInfo.city}, ${visitorInfo.country || ''}`;
  }
  return 'Unknown location';
};

// Helper: Check if session is newly created (within threshold)
const isSessionRecent = (session, thresholdSeconds = 60) => {
  if (!session.created_at) return false;
  const sessionAge = (Date.now() - new Date(session.created_at).getTime()) / 1000;
  return sessionAge < thresholdSeconds;
};

// Helper: Check if session is a new session that needs notification
const isNewSessionForNotification = (session, prevSession, notifiedSessions) => {
  const sessionKey = session.session_id;
  const messageCount = session.message_count || 0;
  const isRecent = isSessionRecent(session, 60);

  return !notifiedSessions.has(sessionKey) &&
    (!prevSession || isRecent) &&
    messageCount <= 2;
};

// Helper: Check if chat was transferred to agent
const isChatTransferredToAgent = (session, prevSession, agentId) => {
  return session.status === 'waiting_for_agent' &&
    prevSession &&
    prevSession.status !== 'waiting_for_agent' &&
    session.assigned_agent === agentId;
};

// Helper: Check if session has new messages for the agent
const hasNewAgentMessage = (session, lastNotifiedCount, agentId, isNewSession) => {
  const isAgentHandling = session.status === 'agent_active' || session.status === 'waiting_for_agent';
  return session.message_count > lastNotifiedCount &&
    isAgentHandling &&
    session.assigned_agent === agentId &&
    !isNewSession;
};

// Helper: Check if user should receive notifications (admin or agent role)
const shouldReceiveNotifications = (agentRole) => {
  return agentRole === 'tenant_admin' || agentRole === 'agent';
};

// Helper: Get visitor name from session
const getVisitorName = (session) => {
  return session.user_data?.name || 'Visitor';
};

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, logout: authLogout } = useAuth();
  const [agent, setAgent] = useState(null);
  const [isOnline, setIsOnline] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [activeVisitors, setActiveVisitors] = useState([]);
  const [newMessageCount, setNewMessageCount] = useState(0);
  const [offlineUnreadCount, setOfflineUnreadCount] = useState(0);
  const [notificationPermission, setNotificationPermission] = useState("default");
  const notifiedSessionsRef = useRef(new Set());
  const notifiedVisitorsRef = useRef(new Set());
  const notifiedMessageCountsRef = useRef(new Map());

  // State for real-time message updates - passed to child pages via outlet context
  const [realtimeMessages, setRealtimeMessages] = useState({ sessionId: null, messages: [], timestamp: 0 });

  const activeTab = getTabFromPath(location.pathname);

  useEffect(() => {
    // Check for new authentication system first
    if (isAuthenticated && user) {
      // Convert new auth user to agent format for compatibility
      setAgent({
        id: user.id,
        username: user.username,
        name: user.name,
        email: user.email,
        role: user.role,
        is_online: true
      });
      setIsOnline(true);
    } else {
      // Fallback to old agent system
      const savedAgent = localStorage.getItem("agent");
      if (savedAgent) {
        const agentData = JSON.parse(savedAgent);
        setAgent(agentData);
        setIsOnline(agentData.is_online || false);
      }
    }

    if ("Notification" in window) {
      setNotificationPermission(Notification.permission);
    }
  }, [isAuthenticated, user]);

  // WebSocket for real-time visitor presence notifications
  useEffect(() => {
    if (!agent) return;

    // Get auth token for WebSocket authentication
    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) {
      logger.log('[AdminLayout] No access token available for visitors WebSocket');
      return;
    }

    const wsBase = BACKEND_URL.replace(/^http/, 'ws');
    const wsUrl = `${wsBase}/api/visitors/stream?token=${encodeURIComponent(accessToken)}`;

    let ws = null;
    let reconnectTimeout = null;

    const connect = () => {
      try {
        ws = new WebSocket(wsUrl);

        ws.onopen = () => {
          logger.log('[AdminLayout] Visitors WebSocket connected');
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);

            if (data.type === 'new_visitor' && data.visitors) {
              data.visitors.forEach(visitor => {
                const visitorId = visitor.visitor_id;

                if (!notifiedVisitorsRef.current.has(visitorId)) {
                  notifiedVisitorsRef.current.add(visitorId);

                  if (shouldReceiveNotifications(agent.role)) {
                    playNotificationSound('/New_Visitor.mp3');
                    const locationStr = getVisitorLocationString(visitor.visitor_info);
                    showBrowserNotification("New Visitor", `New visitor from ${locationStr}`, notificationPermission);
                  }
                }
              });
            } else if (data.type === 'visitors_update' && data.visitors) {
              setActiveVisitors(data.visitors);
            }
          } catch (err) {
            logger.error('[AdminLayout] Error parsing visitors message:', err);
          }
        };

        ws.onerror = (error) => {
          logger.error('[AdminLayout] Visitors WebSocket error:', error);
        };

        ws.onclose = () => {
          logger.log('[AdminLayout] Visitors WebSocket closed, reconnecting...');
          ws = null;
          reconnectTimeout = setTimeout(connect, 3000);
        };

      } catch (err) {
        logger.error('[AdminLayout] Failed to connect visitors WebSocket:', err);
        reconnectTimeout = setTimeout(connect, 3000);
      }
    };

    connect();

    return () => {
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      if (ws) {
        ws.close();
        ws = null;
      }
    };
  }, [agent, notificationPermission]);

  // Keep track of previous sessions for comparison
  const prevSessionsRef = useRef([]);

  // Handle real-time new messages from WebSocket - updates state for child pages
  const handleNewMessages = useCallback((sessionId, messages) => {
    if (sessionId && messages && messages.length > 0) {
      setRealtimeMessages({
        sessionId,
        messages,
        timestamp: Date.now()
      });
    }
  }, []);

  const { sessions: streamedSessions } = useEnhancedSessionsStream(
    agent?.id,
    (updatedSessions) => {
      if (updatedSessions && Array.isArray(updatedSessions)) {
        updatedSessions.forEach(session => {
          const sessionKey = session.session_id;
          const prevSession = prevSessionsRef.current.find(s => s.session_id === sessionKey);

          // Check for new session using helper
          const isNewSession = isNewSessionForNotification(session, prevSession, notifiedSessionsRef.current);

          if (isNewSession) {
            logger.log('[AdminLayout] New session detected:', sessionKey, 'message_count:', session.message_count, 'age:', sessionAge);
            notifiedSessionsRef.current.add(sessionKey);
            // Skip audio - visitor already notified via visitors stream
          }

          // Check if chat was transferred to this agent
          if (isChatTransferredToAgent(session, prevSession, agent?.id)) {
            playNotificationSound('/Incoming_Message.mp3');
            showBrowserNotification(
              "Chat Transferred",
              `Chat from ${getVisitorName(session)} has been assigned to you`,
              notificationPermission
            );
          }

          // Check for new messages on existing sessions
          if (prevSession) {
            const lastNotifiedCount = notifiedMessageCountsRef.current.get(sessionKey) || 0;

            if (hasNewAgentMessage(session, lastNotifiedCount, agent?.id, isNewSession)) {
              notifiedMessageCountsRef.current.set(sessionKey, session.message_count);
              playNotificationSound('/Incoming_Message.mp3');
              showBrowserNotification("New Message", `New message from ${getVisitorName(session)}`, notificationPermission);
            }
          } else {
            notifiedMessageCountsRef.current.set(sessionKey, session.message_count || 0);
          }
        });

        // Update previous sessions ref for next comparison
        prevSessionsRef.current = updatedSessions;
      }
    },
    handleNewMessages  // Third argument: callback for real-time message updates
  );

  useEffect(() => {
    if (streamedSessions && streamedSessions.length > 0) {
      setSessions(streamedSessions);
      // Also update the ref when sessions change
      prevSessionsRef.current = streamedSessions;
    } else if (agent) {
      fetchSessions();
    }
  }, [streamedSessions, agent]);

  const fetchSessions = async () => {
    try {
      // Use access_token from localStorage (new auth system) or fallback to agent.access_token (old system)
      const accessToken = localStorage.getItem('access_token') || agent?.access_token;

      if (!accessToken) {
        logger.log('[AdminLayout] No access token available for fetching sessions');
        return;
      }

      const response = await axios.get(`${API}/sessions`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      setSessions(response.data || []);
    } catch (error) {
      logger.error("Error fetching sessions:", error);
    }
  };

  // Fetch offline messages unread count for sidebar badge
  const fetchOfflineUnreadCount = useCallback(async () => {
    try {
      const accessToken = localStorage.getItem('access_token') || agent?.access_token;
      const tenant = JSON.parse(localStorage.getItem('tenant') || '{}');
      if (!accessToken || !tenant?.id) return;
      const response = await axios.get(`${API}/tenants/${tenant.id}/offline-messages/unread-count`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      setOfflineUnreadCount(response.data?.unread_count || 0);
    } catch {
      // Silently ignore — badge just won't show
    }
  }, [agent]);

  useEffect(() => {
    if (!agent) return;
    fetchOfflineUnreadCount();
    const interval = setInterval(fetchOfflineUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [agent, fetchOfflineUnreadCount]);

  const handleLogin = (agentData) => {
    setAgent(agentData);
    localStorage.setItem("agent", JSON.stringify(agentData));
  };

  const handleLogout = async () => {
    // Use new auth logout if authenticated via new system
    if (isAuthenticated) {
      await authLogout();
      navigate("/login");
    } else {
      // Old system logout
      setAgent(null);
      localStorage.removeItem("agent");
      navigate("/admin");
    }
  };

  const handleStatusToggle = async (checked) => {
    try {
      await axios.post(`${API}/agent/status`, {
        agent_id: agent.id,
        is_online: checked
      });
      setIsOnline(checked);

      const updatedAgent = { ...agent, is_online: checked };
      localStorage.setItem("agent", JSON.stringify(updatedAgent));
      setAgent(updatedAgent);
    } catch (error) {
      logger.error("Error updating status:", error);
    }
  };

  const navigateTo = (path) => {
    navigate(path);
  };

  if (!agent) {
    return <AgentLogin onLogin={handleLogin} />;
  }

  const { title, subtitle } = getPageTitleConfig(activeTab, sessions.length);

  return (
    <div className="h-screen flex bg-gray-50">
      {/* Icon Sidebar */}
      {/* min-h-0 allows children to overflow properly inside a flex container
          nav will handle overflow with overflow-y-auto so the whole page won't scroll */}
      <aside className="w-20 bg-gradient-to-b from-[var(--g6-secondary)] to-[#002A40] flex flex-col items-center py-6 shadow-2xl min-h-0">
        {/* Logo */}
        <div className="mb-8">
          <div className="w-16 h-16 rounded-2xl bg-white flex items-center justify-center shadow-lg overflow-hidden">
            <img
              src="/Logo.png"
              alt="G6th Sense"
              className="w-14 h-14 object-contain scale-110"
            />
          </div>
        </div>

        {/* Navigation Icons (scrollable) */}
        <div className="relative flex-1 w-full min-h-0">
          {/* subtle fade overlays to hint at scrollable content (no visible scrollbar) */}
          <div className="absolute inset-x-0 top-0 h-6 pointer-events-none bg-gradient-to-b from-black/10 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 h-6 pointer-events-none bg-gradient-to-t from-black/10 to-transparent" />
          {/* sidebar nav (manual buttons retained) */}
          <nav className="h-full flex flex-col gap-2 w-full px-2 overflow-y-auto min-h-0 no-scrollbar">
            {/* buttons start */}
            <button
              onClick={() => navigateTo("/admin")}
              className={`group relative p-4 rounded-xl transition-all duration-200 ${activeTab === "dashboard"
                ? "bg-white/20 shadow-lg"
                : "hover:bg-white/10"
                }`}
              title="Dashboard"
            >
              <BarChart3 className={`w-6 h-6 mx-auto ${activeTab === "dashboard" ? "text-white" : "text-gray-300"
                }`} />
              <div className="absolute left-full ml-4 px-3 py-1.5 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity z-50">
                Dashboard
              </div>
            </button>

            <button
              onClick={() => navigateTo("/admin/visitors")}
              className={`group relative p-4 rounded-xl transition-all duration-200 ${activeTab === "visitors"
                ? "bg-white/20 shadow-lg"
                : "hover:bg-white/10"
                }`}
              title="Visitors"
            >
              <Eye className={`w-6 h-6 mx-auto ${activeTab === "visitors" ? "text-white" : "text-gray-300"
                }`} />
              <div className="absolute left-full ml-4 px-3 py-1.5 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity z-50">
                Visitors
              </div>
            </button>

            <button
              onClick={() => navigateTo("/admin/traffic")}
              className={`group relative p-4 rounded-xl transition-all duration-200 ${activeTab === "traffic"
                ? "bg-white/20 shadow-lg"
                : "hover:bg-white/10"
                }`}
              title="Traffic"
            >
              <TrendingUp className={`w-6 h-6 mx-auto ${activeTab === "traffic" ? "text-white" : "text-gray-300"
                }`} />
              <div className="absolute left-full ml-4 px-3 py-1.5 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity z-50">
                Traffic
              </div>
            </button>

            <button
              onClick={() => navigateTo("/admin/chats")}
              className={`group relative p-4 rounded-xl transition-all duration-200 ${activeTab === "chats"
                ? "bg-white/20 shadow-lg"
                : "hover:bg-white/10"
                }`}
              title="Chats"
            >
              <MessageCircle className={`w-6 h-6 mx-auto ${activeTab === "chats" ? "text-white" : "text-gray-300"
                }`} />
              {sessions.length > 0 && (
                <span className="absolute top-2 right-2 w-5 h-5 bg-[var(--g6-primary)] text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {sessions.length > 9 ? '9+' : sessions.length}
                </span>
              )}
              <div className="absolute left-full ml-4 px-3 py-1.5 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity z-50">
                Chats
              </div>
            </button>

            <button
              onClick={() => navigateTo("/admin/knowledge")}
              className={`group relative p-4 rounded-xl transition-all duration-200 ${activeTab === "knowledge"
                ? "bg-white/20 shadow-lg"
                : "hover:bg-white/10"
                }`}
              title="Knowledge"
            >
              <BookOpen className={`w-6 h-6 mx-auto ${activeTab === "knowledge" ? "text-white" : "text-gray-300"
                }`} />
              <div className="absolute left-full ml-4 px-3 py-1.5 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity z-50">
                Knowledge
              </div>
            </button>

            <button
              onClick={() => navigateTo("/admin/feedback")}
              className={`group relative p-4 rounded-xl transition-all duration-200 ${activeTab === "feedback"
                ? "bg-white/20 shadow-lg"
                : "hover:bg-white/10"
                }`}
              title="Feedback"
            >
              <MessageSquare className={`w-6 h-6 mx-auto ${activeTab === "feedback" ? "text-white" : "text-gray-300"
                }`} />
              <div className="absolute left-full ml-4 px-3 py-1.5 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity z-50">
                Feedback
              </div>
            </button>

            <button
              onClick={() => navigateTo("/admin/offline-messages")}
              className={`group relative p-4 rounded-xl transition-all duration-200 ${activeTab === "offline-messages"
                ? "bg-white/20 shadow-lg"
                : "hover:bg-white/10"
                }`}
              title="Offline Messages"
            >
              <Mail className={`w-6 h-6 mx-auto ${activeTab === "offline-messages" ? "text-white" : "text-gray-300"
                }`} />
              {offlineUnreadCount > 0 && (
                <span className="absolute top-2 right-2 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1">
                  {offlineUnreadCount > 9 ? '9+' : offlineUnreadCount}
                </span>
              )}
              <div className="absolute left-full ml-4 px-3 py-1.5 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity z-50">
                Offline Messages
              </div>
            </button>

            <button
              onClick={() => navigateTo("/admin/settings")}
              className={`group relative p-4 rounded-xl transition-all duration-200 ${activeTab === "settings"
                ? "bg-white/20 shadow-lg"
                : "hover:bg-white/10"
                }`}
              title="Settings"
            >
              <Settings className={`w-6 h-6 mx-auto ${activeTab === "settings" ? "text-white" : "text-gray-300"
                }`} />
              <div className="absolute left-full ml-4 px-3 py-1.5 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity z-50">
                Settings
              </div>
            </button>

            <button
              onClick={() => navigateTo("/admin/personas")}
              className={`group relative p-4 rounded-xl transition-all duration-200 ${activeTab === "personas"
                ? "bg-white/20 shadow-lg"
                : "hover:bg-white/10"
                }`}
              title="Bot Personas"
            >
              <UserCircle className={`w-6 h-6 mx-auto ${activeTab === "personas" ? "text-white" : "text-gray-300"
                }`} />
              <div className="absolute left-full ml-4 px-3 py-1.5 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity z-50">
                Personas
              </div>
            </button>

            <button
              onClick={() => navigateTo("/admin/account")}
              className={`group relative p-4 rounded-xl transition-all duration-200 ${activeTab === "account"
                ? "bg-white/20 shadow-lg"
                : "hover:bg-white/10"
                }`}
              title="Account"
            >
              <KeyRound className={`w-6 h-6 mx-auto ${activeTab === "account" ? "text-white" : "text-gray-300"
                }`} />
              <div className="absolute left-full ml-4 px-3 py-1.5 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity z-50">
                Account
              </div>
            </button>

            {/* Agents - Only visible to tenant_admin */}
            {agent?.role === "tenant_admin" && (
              <button
                onClick={() => navigateTo("/admin/agents")}
                className={`group relative p-4 rounded-xl transition-all duration-200 ${activeTab === "agents"
                  ? "bg-white/20 shadow-lg"
                  : "hover:bg-white/10"
                  }`}
                title="Agents"
              >
                <Users className={`w-6 h-6 mx-auto ${activeTab === "agents" ? "text-white" : "text-gray-300"
                  }`} />
                <div className="absolute left-full ml-4 px-3 py-1.5 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity z-50">
                  Agents
                </div>
              </button>
            )}

            {/* Maintenance - Only visible to tenant_admin */}
            {agent?.role === "tenant_admin" && (
              <button
                onClick={() => navigateTo("/admin/maintenance")}
                className={`group relative p-4 rounded-xl transition-all duration-200 ${activeTab === "maintenance"
                  ? "bg-white/20 shadow-lg"
                  : "hover:bg-white/10"
                  }`}
                title="Maintenance"
              >
                <Database className={`w-6 h-6 mx-auto ${activeTab === "maintenance" ? "text-white" : "text-gray-300"
                  }`} />
                <div className="absolute left-full ml-4 px-3 py-1.5 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity z-50">
                  Maintenance
                </div>
              </button>
            )}
            {/* buttons end */}
          </nav>
        </div>

        {/* Bottom Actions */}
        <div className="flex flex-col gap-2 w-full px-2 pt-4 border-t border-white/10">
          {/* Status Indicator */}
          <button
            onClick={() => handleStatusToggle(!isOnline)}
            className="group relative p-4 rounded-xl hover:bg-white/10 transition-all"
            title={isOnline ? "Online" : "Offline"}
          >
            <div className={`w-6 h-6 mx-auto rounded-full border-2 border-white ${isOnline ? 'bg-green-500' : 'bg-gray-500'
              }`} />
            <div className="absolute left-full ml-4 px-3 py-1.5 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity z-50">
              {isOnline ? 'Online' : 'Offline'}
            </div>
          </button>

          {/* Agent Profile */}
          <div className="group relative p-2">
            <div className="w-12 h-12 mx-auto rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-semibold text-lg border-2 border-white/20">
              {agent.name?.[0]?.toUpperCase()}
            </div>
            <div className="absolute left-full ml-4 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity z-50">
              <p className="font-semibold">{agent.name}</p>
              <p className="text-xs text-gray-300">{agent.role}</p>
            </div>
          </div>

          {/* Logout */}
          <button
            onClick={handleLogout}
            className="group relative p-4 rounded-xl hover:bg-red-500/20 transition-all"
            title="Logout"
          >
            <LogOut className="w-6 h-6 mx-auto text-red-400" />
            <div className="absolute left-full ml-4 px-3 py-1.5 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity z-50">
              Logout
            </div>
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 min-w-0 flex flex-col overflow-hidden">
        {/* Top Header Bar */}
        <header className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Branding */}
              <div className="flex items-center gap-3 pr-4 border-r border-gray-200">
                <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center overflow-hidden">
                  <img
                    src="/Logo_Black.png"
                    alt="G6th Sense"
                    className="w-10 h-10 object-contain scale-125"
                  />
                </div>
                <div className="hidden sm:block">
                  <p className="text-sm font-bold text-gray-900">G6th Sense</p>
                  <p className="text-xs text-gray-500">Admin Panel</p>
                </div>
              </div>
              {/* Page Title */}
              <div>
                <h1 className="text-xl font-bold text-gray-900">{title}</h1>
                <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-50">
                <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-green-500' : 'bg-gray-400'} animate-pulse`} />
                <span className="text-sm font-medium text-gray-700">{isOnline ? 'Online' : 'Offline'}</span>
              </div>
            </div>
          </div>
        </header>

        {/* Content - Outlet renders the child route */}
        {/* Allow child route pages to scroll when their content is larger than the viewport */}
        <div className="flex-1 min-h-0 overflow-auto">
          <Outlet context={{ agent, sessions, fetchSessions, isOnline, activeVisitors, realtimeMessages }} />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;